/**
 * Authentication Service
 *
 * Handles login, logout, token refresh, and user session management.
 */

import apiClient, {
  APIResponse,
  tokenStorage,
  unwrapResponse,
  ApiError,
} from './apiClient';
import { ENDPOINTS, TOKEN_KEYS } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: 'ADMIN' | 'GERANT' | 'COMMERCIAL' | 'COMPTABLE' | 'MAGASINIER' | 'TECHNICIEN';
  is_superuser: boolean;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: AuthUser;
}

export interface TokenRefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

// =============================================================================
// User Storage
// =============================================================================

function saveUser(user: AuthUser): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(user));
  }
}

function getStoredUser(): AuthUser | null {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(TOKEN_KEYS.USER);
    if (stored) {
      try {
        return JSON.parse(stored) as AuthUser;
      } catch {
        return null;
      }
    }
  }
  return null;
}

function clearUser(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEYS.USER);
  }
}

// =============================================================================
// Auth Service
// =============================================================================

export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<TokenResponse> {
    const response = await apiClient.post<APIResponse<TokenResponse>>(
      ENDPOINTS.AUTH.LOGIN,
      credentials
    );

    const data = unwrapResponse(response);

    // Store tokens and user
    tokenStorage.setTokens(data.access_token, data.refresh_token);
    saveUser(data.user);

    return data;
  },

  /**
   * Refresh access token using refresh token
   */
  async refresh(): Promise<TokenRefreshResponse> {
    const refreshToken = tokenStorage.getRefreshToken();

    if (!refreshToken) {
      throw new ApiError('No refresh token available', 'NO_REFRESH_TOKEN', 401);
    }

    const response = await apiClient.post<APIResponse<TokenRefreshResponse>>(
      ENDPOINTS.AUTH.REFRESH,
      { refresh_token: refreshToken }
    );

    const data = unwrapResponse(response);

    // Update stored tokens
    tokenStorage.setTokens(data.access_token, data.refresh_token);

    return data;
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken();

    try {
      if (refreshToken) {
        await apiClient.post(ENDPOINTS.AUTH.LOGOUT, {
          refresh_token: refreshToken,
        });
      }
    } catch (error) {
      // Ignore errors during logout - we'll clear tokens anyway
      console.warn('Logout API call failed:', error);
    } finally {
      // Always clear local state
      tokenStorage.clearTokens();
      clearUser();
    }
  },

  /**
   * Logout from all sessions
   */
  async logoutAll(): Promise<number> {
    const response = await apiClient.post<APIResponse<{ sessions_revoked: number }>>(
      ENDPOINTS.AUTH.LOGOUT + '-all'
    );

    const data = unwrapResponse(response);

    // Clear local state
    tokenStorage.clearTokens();
    clearUser();

    return data.sessions_revoked;
  },

  /**
   * Get current user from API
   */
  async getCurrentUser(): Promise<AuthUser> {
    const response = await apiClient.get<APIResponse<AuthUser>>(ENDPOINTS.AUTH.ME);
    const user = unwrapResponse(response);
    saveUser(user);
    return user;
  },

  /**
   * Change current user's password
   */
  async changePassword(request: ChangePasswordRequest): Promise<void> {
    await apiClient.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, request);
  },

  /**
   * Request a password reset link by email. Always returns success
   * (the backend never reveals whether the email exists).
   */
  async forgotPassword(email: string): Promise<void> {
    await apiClient.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
  },

  /**
   * Consume a password reset token and set a new password.
   */
  async resetPassword(token: string, new_password: string): Promise<void> {
    await apiClient.post(ENDPOINTS.AUTH.RESET_PASSWORD, { token, new_password });
  },

  /**
   * Get stored user (from localStorage)
   */
  getUser(): AuthUser | null {
    return getStoredUser();
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return tokenStorage.hasTokens();
  },

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return tokenStorage.getAccessToken();
  },

  /**
   * Check if user has specific role
   */
  hasRole(role: AuthUser['role']): boolean {
    const user = getStoredUser();
    return user?.role === role;
  },

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: AuthUser['role'][]): boolean {
    const user = getStoredUser();
    return user ? roles.includes(user.role) : false;
  },

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    const user = getStoredUser();
    return user?.role === 'ADMIN' || user?.is_superuser === true;
  },
};

// =============================================================================
// Auth Event Listener
// =============================================================================

// Listen for logout events from apiClient
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    tokenStorage.clearTokens();
    clearUser();
    // Optionally redirect to login
    // window.location.href = '/login';
  });
}

export default authService;
